import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FORM_ERROR } from "final-form";

import { Footer } from "../../components/Footer";
import { HeaderWrapper } from "../../components/Header/HeaderWrapper";
import { FoundingPartnerSection, TierValue, TIER_VALUES } from "../../components/LaunchPackage/FoundingPartnerSection";
import { FreeDemoSection } from "../../components/LaunchPackage/FreeDemoSection";
import { actions as emailActions } from "coupon-common/src/modules/email";
import { dispatchAsAsync } from "coupon-common/src/utils";

// ─── Shared visual primitives (mirrors the design language used across
// Page1/Page2/Page3: red dot + label, rounded-full red gradient buttons,
// rounded-[30px] soft-shadow cards) ─────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <div className="flex items-center justify-center gap-3">
    <span className="w-[14px] h-[14px] rounded-full bg-[#D90000]" />
    <span className={`text-[16px] min-[1442px]:text-[18px] leading-none font-medium ${light ? "text-white" : "text-[#2A2A2A]"}`}>
      {children}
    </span>
  </div>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const TIER_LABELS: Record<TierValue, string> = {
  pioneer: "Pioneer ($99)",
  growth: "Growth Partner ($319)",
  founding: "Founding Partner ($485) - Best Value",
};

// ─── Hero testimonials (auto-rotating) ─────────────────────────────────────

interface HeroTestimonial {
  quote: string;
  name: string;
  title: string;
  avatar?: string;
}

const HERO_TESTIMONIALS: HeroTestimonial[] = [
  {
    quote:
      "What Nelson is building with My Couponbook is exactly what local businesses need. It's a practical tool that drives real value for small businesses and strengthens the communities they serve. I've been impressed by Nelson's vision and the genuine impact this platform can have at the local level.",
    name: "David Rafuse",
    title: "CEO/Founder, Blended Athletics Inc.",
    avatar: "/images/Webp/DaviRafusePP.webp",
  },
  {
    quote:
      "Between high fees and subscriptions from other apps, it's hard for local shops to get ahead. The fact that My Couponbook gives us a free platform to connect directly with our neighborhood shows they actually want to be a true partner to small businesses, rather than just another monthly expense.",
    name: "Joel George",
    title: "Owner, Fresco Italiano",
    avatar: "/images/Webp/Joel George.webp",
  },
  {
    quote:
      "Working with My Couponbook has been great! Nelson is supportive and friendly and accommodates our changes when needed. I would encourage any business to use the app!",
    name: "Dejhani Allen",
    title: "Owner, Brawta Jamaican Jerk Joint",
    avatar: "/images/Webp/Dejhani Allen.webp",
  },
  {
    quote:
      "Instead of paying for expensive local ads, My Couponbook lets us connect with our customers and pass marketing savings directly to them. We stay busy, and they get the best value.",
    name: "Jon Bowman",
    title: "Owner, Valley West Auto",
    avatar: "/images/Webp/JonBowman.webp",
  },
  {
    quote:
      "My Couponbook completely changed how we attract local customers. Instead of waiting for walk-ins, we now reach people at the right moment. The push notifications alone bring in new customers every week.",
    name: "Robert Johnson",
    title: "Owner, The Corner Bakery",
    avatar: "/images/Webp/ellipse-3.webp",
  },
];

// ─── Core feature rows ──────────────────────────────────────────────────────

interface FeatureRow {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  paragraphs: string[];
  image: string;
  imageAlt: string;
  align?: "left" | "right";
}

const ConnectionsIcon = () => (
  <svg className="w-5 h-5 text-brandRed" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const BarChartIcon = () => (
  <svg className="w-5 h-5 text-brandRed" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20V10m5 10V4m5 16v-7" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5 text-brandRed" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CrownIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 8l3.5 3L12 5l5.5 6L21 8l-2 10H5L3 8zm4.2 9h9.6l1.13-5.66-1.72 1.48a1 1 0 01-1.45-.15L12 8.32l-2.76 4.35a1 1 0 01-1.45.15l-1.72-1.48L7.2 17z" />
  </svg>
);

const SmallDollarIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const PeopleSmallIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const ReelIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 8h16M4 8v10a1 1 0 001 1h14a1 1 0 001-1V8M4 8l1.5-4h13L20 8M9 4L7.5 8M15 4l-1.5 4"
    />
  </svg>
);

// Tablet grid cells for the package perks: two left of the Founding Partner card, two right.
const TABLET_PERK_CELLS = [
  "md:max-lg:col-start-1 md:max-lg:row-start-3 md:max-lg:mt-5",
  "md:max-lg:col-start-3 md:max-lg:row-start-3 md:max-lg:mt-5",
  "md:max-lg:col-start-1 md:max-lg:row-start-4",
  "md:max-lg:col-start-3 md:max-lg:row-start-4",
];

const LAUNCH_PACKAGE_PERKS = [
  { icon: <CrownIcon className="w-5 h-5 text-white" />, label: "Premium", sub: "Premium Membership" },
  { icon: <SmallDollarIcon className="w-5 h-5 text-white" />, label: "$500", sub: "Marketing Credits" },
  { icon: <PeopleSmallIcon className="w-5 h-5 text-white" />, label: "200", sub: "Invites" },
  { icon: <ReelIcon className="w-5 h-5 text-white" />, label: "Reel", sub: "Influencer Reel" },
];

const ChartIcon = () => (
  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const FEATURE_ROWS: FeatureRow[] = [
  {
    icon: <ConnectionsIcon />,
    iconBg: "bg-red-100",
    title: "Build Lasting Connections.",
    paragraphs: [
      "Grow a following of customers who actively love to support local. Every new follower gives you a direct line to bring them back, allowing you to send instant push notifications right to their phones the second you publish a deal.",
    ],
    image: "/images/launch/feature-connections.webp",
    imageAlt: "Business owner helping a customer in their shop",
    align: "left",
  },
  {
    icon: <BarChartIcon />,
    iconBg: "bg-red-100",
    title: "AI Generated Coupons",
    paragraphs: [
      "Let our AI instantly generate proven, high-converting coupons so you can publish deals in minutes. Every redemption is tracked in your dashboard, giving you the clear data you need to maximize your ROI.",
    ],
    image: "/images/launch/feature-ai-coupons-collage.webp",
    imageAlt: "Business dashboard and team reviewing coupon performance",
    align: "right",
  },
  {
    icon: <DollarIcon />,
    iconBg: "bg-red-100",
    title: "Zero-Commissions. Effortless Redemption.",
    paragraphs: [
      "Maximize your profits while keeping the checkout process completely stress-free for your staff. Customers just activate the deal on their phone, show you the screen, and pay you directly. Pay no commissions!",
    ],
    image: "/images/launch/feature-zero-commission.webp",
    imageAlt: "Business owner and customer reviewing zero commission earnings",
    align: "left",
  },
];

// ─── Bonus / gift rows ──────────────────────────────────────────────────────

interface BonusRow {
  label: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  callout?: string;
}

const BONUS_ROWS: BonusRow[] = [
  {
    label: "Marketing Capital",
    title: "$500 in Marketing Credits",
    description:
      "Instantly loaded into your account as 50,000 Points. Use this massive reserve to fund your premium push notifications and in-app campaigns to your followers. Fuel your paid marketing right out of the gate and ensure your deals are seen immediately.",
    image: "/images/launch/bonus-marketing-credits.webp",
    imageAlt: "Marketing campaign assets and content pieces",
  },
  {
    label: "Brand Exposure",
    title: "Local Influencers & Social Marketing",
    description:
      "Don't market your deals alone. We care about your success. When you launch, we will run targeted Meta ads and dedicated local influencer campaigns specifically designed to champion your business to the community.",
    image: "/images/launch/bonus-influencer.webp",
    imageAlt: "Local influencer filming content at a restaurant",
  },
  {
    label: "In-Store Conversion",
    title: "Elite Print Kit & Referral Engine",
    description:
      "We equip you for success. When a customer scans your counter display, they get the gift of a free Premium membership and are instantly prompted to follow your business.",
    image: "/images/launch/bonus-print-kit.webp",
    imageAlt: "Elite print marketing kit box with materials",
    callout:
      "You earn points for every referral. With this engine, you can easily earn over $1,000 in marketing credits every single year.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Is this a set-it-and-forget-it marketing tool?",
    a: "No. My Couponbook is a dynamic community platform. It works best for visionary owners who actively publish deals, engage with their followers, and use our tools to drive real-world foot traffic. If you are willing to spend a few minutes a week posting deals, we will provide the audience.",
  },
  {
    q: "Is My Couponbook free?",
    a: "Yes, you can sign up for a basic account completely free, but you will miss out on the early stage launch bonuses that give you the exact hands-on support, marketing capital, and priority visibility you need to launch with massive momentum.",
  },
  {
    q: "Do you take a cut of my sales?",
    a: "No, customers pay you directly at the counter. We take zero commission on your sales.",
  },
  {
    q: "How do the marketing credits work?",
    a: "They are loaded into your account as points, which you use to actively send instant Push Notifications to your followers or fund in-app promotions, ensuring your deals are seen immediately.",
  },
  {
    q: "How do customers redeem coupons?",
    a: "Customers activate the deal in the app and show the redemption screen at the counter. There's no POS integration or complex hardware required on your end.",
  },
  {
    q: "Can I set a limit to how many coupons are redeemed per customer?",
    a: "Yes, you have full control over redemption limits per customer and per campaign directly from your business dashboard.",
  },
];

const HeroTestimonialSlider: React.FC = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % HERO_TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const active = HERO_TESTIMONIALS[index];

  const buildNodes = () => (
    <>
      <div
        key={index}
        className="relative bg-black/55 backdrop-blur-md rounded-[20px] border border-white/15 shadow-[0px_20px_50px_rgba(0,0,0,0.35)] px-7 py-6"
        style={{ animation: "heroTestimonialSlideFade 0.6s ease" }}
      >
        <p className="text-white/95 text-[15px] min-[1442px]:text-[17px] italic leading-relaxed">"{active.quote}"</p>
        <div className="mt-3 flex items-center gap-3">
          {active.avatar ? (
            <img src={active.avatar} alt={active.name} className="w-9 h-9 min-[1442px]:w-10 min-[1442px]:h-10 rounded-full object-cover" />
          ) : (
            <span className="flex items-center justify-center w-9 h-9 min-[1442px]:w-10 min-[1442px]:h-10 rounded-full bg-white/15 text-white text-sm font-bold shrink-0">
              {active.name.charAt(0)}
            </span>
          )}
          <div>
            <p className="text-white text-[14px] min-[1442px]:text-[16px] font-semibold leading-none m-0">{active.name}</p>
            <p className="text-white/70 text-[12px] min-[1442px]:text-[14px] leading-none m-0 mt-2">{active.title}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        {HERO_TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show testimonial ${i + 1}`}
            onClick={() => setIndex(i)}
            className={`h-[6px] rounded-full transition-all duration-300 ${
              i === index ? "w-6 bg-white" : "w-[6px] bg-white/40"
            }`}
          />
        ))}
      </div>
    </>
  );

  return (
    <>
      <style>{`
        @keyframes heroTestimonialSlideFade {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Mobile: absolute, positioned below the CTA button with a clear gap */}
      <div className="md:hidden absolute left-6 right-6 z-10" style={{ top: 620 }}>
        {buildNodes()}
      </div>

      {/* Tablet: absolute, right-aligned, positioned below the CTA button with a clear gap */}
      <div
        className="hidden md:block lg:hidden absolute right-6 z-10 w-[380px] max-w-[calc(100vw-3rem)]"
        style={{ top: 640 }}
      >
        {buildNodes()}
      </div>

      {/* Desktop: original bottom-right overlay */}
      <div className="hidden lg:block absolute bottom-44 right-6 lg:right-16 z-10 w-[420px] max-w-[calc(100vw-3rem)]">
        {buildNodes()}
      </div>
    </>
  );
};

// `location` is the place named in the hero ("We are coming to …") — one
// route per location in App.js renders this same page with a different name.
export const LaunchPackage: React.FC<{ location?: string }> = ({ location = "Moncton" }) => {
  const [tier, setTier] = useState<TierValue>("founding");
  const [openFaq, setOpenFaq] = useState<number>(0);
  const [isApplyOpen, setIsApplyOpen] = useState(false);

  const selectTier = (value: TierValue) => {
    setTier(value);
    setIsApplyOpen(true);
  };

  useEffect(() => {
    const requested = new URLSearchParams(window.location.search).get("apply") as TierValue | null;
    if (requested && TIER_VALUES.includes(requested)) {
      selectTier(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isApplyOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isApplyOpen]);

  return (
    <div className="relative w-full bg-white overflow-x-hidden min-h-screen">
      <HeaderWrapper />

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full h-[960px] lg:h-[900px] flex items-center">
          <img
            src="/images/launch/hero-bar.webp"
            alt="Bartender serving customers at a local bar"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />

          <div className="relative z-10 w-full h-full max-w-[1440px] mx-auto">
            <div className="max-w-[720px] min-[1442px]:max-w-[780px] px-12 pt-36 min-[1441px]:pt-[200px]">
              <div className="flex items-center gap-3">
                <span className="text-[18px] min-[1442px]:text-[23px] leading-none">🍁</span>
                <span className="text-white text-[16px] min-[1442px]:text-[20px] font-semibold">
                  Canada's All-In-One Coupon and Rewards App.
                </span>
              </div>

              <h1 className="mt-6 text-white text-[54px] sm:text-[72px] min-[1442px]:text-[68px] min-[1442px]:sm:text-[90px] leading-[1.08] font-light tracking-[-1.5px]">
                We are coming to
                <br />
                <span className="text-[#E00000]">{location}</span>
              </h1>
              <p className="mt-2 text-white/90 text-[22px] sm:text-[26px] min-[1442px]:text-[27px] min-[1442px]:sm:text-[32px] font-thin leading-[1.3] max-w-[640px] min-[1442px]:max-w-[680px]">
                Be the first to join and get the perks of a
                <br />
                Founding Partner.
              </p>

              <button
                type="button"
                onClick={() => setIsApplyOpen(true)}
                className="inline-flex items-center gap-3 mt-10 bg-white/10 backdrop-blur-sm border border-white/25 text-white no-underline rounded-full font-medium hover:bg-white/20 transition px-8 py-4 text-[16px] min-[1442px]:px-9 min-[1442px]:py-[18px] min-[1442px]:text-[19px]"
              >
                Apply to Launch with us
              </button>
            </div>

            <HeroTestimonialSlider />
          </div>
        </div>
      </section>

      {/* ── CORE FEATURES ── */}
      <section className="w-full flex justify-center py-24 bg-white" id="how-it-works">
        <div className="w-full max-w-[1440px] px-[21px] md:px-8 lg:px-12">
          <SectionLabel>Designed for Results That Sell</SectionLabel>
          <h2 className="mt-3 text-center text-[#171717] text-[30px] sm:text-[40px] min-[1442px]:text-[38px] min-[1442px]:sm:text-[50px] leading-[1.15] font-medium tracking-[-1px] max-w-[720px] mx-auto">
            Marketing that <span className="text-[#E00000] font-bold">brings them back.</span>
          </h2>
          <p className="mt-2 text-center text-[#171717] text-[14px] min-[1442px]:text-[18px] font-semibold max-w-[680px] min-[1442px]:max-w-[720px] mx-auto leading-relaxed capitalize">
            A digital coupon on My Couponbook doesn't just offer a discount—it creates a lasting
            connection, bringing local supporters through your doors more often.
          </p>

          {(() => {
            const leftColumn: React.ReactNode[] = [];
            const rightColumn: React.ReactNode[] = [];
            const stackedRows: React.ReactNode[] = [];

            FEATURE_ROWS.forEach((row, i) => {
              const isCentered = row.align === "right";

              const imagePairsWithLeftAlignedText = i % 2 === 0;

              const imageNode = (
                <div
                  key={`image-${i}`}
                  className={`w-full max-w-[460px] min-[1442px]:max-w-[520px] aspect-square rounded-[20px] overflow-hidden shadow-[0px_8px_28px_rgba(0,0,0,0.08)] ${
                    imagePairsWithLeftAlignedText ? "ml-auto" : ""
                  }`}
                >
                  <img src={row.image} alt={row.imageAlt} className="w-full h-full object-cover" />
                </div>
              );

              const textNode = (
                <div key={`text-${i}`} className={isCentered ? "text-right flex flex-col items-end ml-auto" : "text-left"}>
                  <div
                    className={`inline-flex items-center justify-center h-12 w-12 min-[1442px]:h-14 min-[1442px]:w-14 rounded-full ${row.iconBg} mb-6 shadow-sm`}
                  >
                    {row.icon}
                  </div>
                  {/* Descriptions take the title's width (w-0 + min-w-full keeps them from widening the wrapper), with a floor so short titles don't squeeze them */}
                  <div className="w-fit min-w-[400px] min-[1442px]:min-w-[460px] max-w-full">
                    <h3 className="text-[24px] min-[1442px]:text-[29px] font-bold text-gray-900 mb-4">{row.title}</h3>
                    {row.paragraphs.map((p, pi) => (
                      <p
                        key={pi}
                        className="w-0 min-w-full text-gray-600 text-[16px] min-[1442px]:text-[19px] leading-relaxed mb-4 last:mb-0 capitalize"
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              );

              if (i % 2 === 0) {
                leftColumn.push(imageNode);
                rightColumn.push(textNode);
              } else {
                rightColumn.push(imageNode);
                leftColumn.push(textNode);
              }

              // Mobile/tablet: text block always on top of the image, in the row's natural order.
              stackedRows.push(
                <div key={`row-${i}`} className="flex flex-col items-center text-center">
                  <div className="flex flex-col items-center w-fit max-w-full">
                    <div
                      className={`inline-flex items-center justify-center h-12 w-12 min-[1442px]:h-14 min-[1442px]:w-14 rounded-full ${row.iconBg} mb-3 shadow-sm`}
                    >
                      {row.icon}
                    </div>
                    <h3 className="text-[24px] min-[1442px]:text-[29px] font-bold text-gray-900 mb-4">{row.title}</h3>
                    {row.paragraphs.map((p, pi) => (
                      <p
                        key={pi}
                        className="w-0 min-w-full text-gray-600 text-[16px] min-[1442px]:text-[19px] leading-relaxed mb-4 last:mb-0 capitalize"
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                  <div className="w-full max-w-[540px] aspect-square rounded-[20px] overflow-hidden shadow-[0px_8px_28px_rgba(0,0,0,0.08)] mt-2">
                    <img src={row.image} alt={row.imageAlt} className="w-full h-full object-cover" />
                  </div>
                </div>
              );
            });

            return (
              <>
                {/* Mobile/tablet: stacked, text above image, true row order */}
                <div className="mt-16 flex flex-col gap-16 lg:hidden">{stackedRows}</div>

                {/* Desktop: zigzag columns */}
                <div className="mt-16 hidden lg:flex lg:flex-row gap-16 lg:gap-16">
                  <div className="lg:w-1/2 w-full flex flex-col gap-16 lg:gap-24">{leftColumn}</div>
                  <div className="lg:w-1/2 w-full flex flex-col gap-16 lg:gap-24 lg:mt-40">{rightColumn}</div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ── FOUNDING PARTNER ANCHOR (dark) ── */}
      <section className="relative bg-gray-900 overflow-hidden" id="the-package">
        <div className="relative pt-24 pb-20 z-10 border-b border-white/10">
          <img
            src="/images/launch/founding-package-bg.webp"
            alt="Customers and staff inside a busy local restaurant"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/55" />

          <div className="relative max-w-[1440px] mx-auto px-6 text-center">
            <h2 className="text-[30px] sm:text-[40px] min-[1442px]:text-[38px] min-[1442px]:sm:text-[50px] leading-[1.15] font-medium tracking-[-1px] mb-10 text-white">
              The Founding Partner Launch Package
            </h2>

            <div className="relative max-w-[1240px] mx-auto rounded-[36px] border border-white/25 p-8 md:p-12 lg:px-8 xl:px-12 text-left">
              <div className="flex flex-wrap items-center max-lg:justify-center max-lg:text-center gap-x-4 gap-y-1">
                <span className="text-white text-[56px] md:text-[76px] min-[1442px]:text-[68px] min-[1442px]:md:text-[95px] font-black leading-none tracking-tight">
                  $485
                </span>
                <span className="text-white text-[26px] md:text-[32px] min-[1442px]:text-[32px] min-[1442px]:md:text-[41px] font-light leading-tight pb-1">
                  Includes 2 Years of
                  <br />
                  <span className="text-[#F5C518] font-semibold">Premium Membership</span>
                </span>
              </div>

              {/* Tablet (md–lg): 3-column grid — two perks, the Founding Partner card, two perks.
                  The text block and perks list use display:contents there so their children
                  become direct grid items alongside the card. */}
              <div className="mt-8 flex flex-col lg:flex-row items-center justify-start gap-8 xl:gap-12 md:max-lg:grid md:max-lg:grid-cols-[1fr_auto_1fr] md:max-lg:gap-x-6 md:max-lg:gap-y-3">
                <div className="self-center order-[2] lg:order-[1] shrink-0 md:max-lg:col-start-2 md:max-lg:row-start-3 md:max-lg:row-span-2 md:max-lg:mt-5">
                  <div className="bg-white/[0.08] backdrop-blur-2xl backdrop-saturate-150 rounded-[24px] px-8 py-8 text-center w-[220px] min-[1442px]:w-[240px] border border-white/25 shadow-[0px_8px_32px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.15)]">
                    <CrownIcon className="w-7 h-7 text-[#F5C518] mx-auto mb-3" />
                    <p className="text-white text-[14px] min-[1442px]:text-[16px] mb-1">First 10 To Join</p>
                    <p className="text-white text-[22px] min-[1442px]:text-[25px] font-black uppercase leading-tight mb-5">
                      Founding
                      <br />
                      Partner
                    </p>
                    <span className="inline-block bg-[#E00000] text-white text-[11px] min-[1442px]:text-[12px] font-bold uppercase tracking-wider rounded-full px-4 py-1.5">
                      In Your City
                    </span>
                  </div>
                </div>

                <div className="order-[1] lg:order-[2] flex-1 w-full max-lg:text-center md:max-lg:contents">
                  <p className="md:max-lg:col-span-3 text-white text-[18px] md:text-[20px] min-[1442px]:text-[20px] min-[1442px]:md:text-[23px]">
                    Total Package Value : <span className="font-bold">Over $2,000</span>
                  </p>
                  <p className="md:max-lg:col-span-3 md:max-lg:mt-0 mt-3 text-gray-300 opacity-80 text-[14px] md:text-[15px] min-[1442px]:text-[16px] min-[1442px]:md:text-[17px] max-w-2xl max-lg:mx-auto leading-relaxed">
                    This exclusive launch opportunity is only available for a strictly limited number of
                    applicants per city during our pre-launch phase. Secure your spot today.
                  </p>

                  <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 lg:flex lg:flex-nowrap lg:gap-x-4 xl:gap-x-8 md:max-lg:contents">
                    {LAUNCH_PACKAGE_PERKS.map((perk, i) => (
                      <div
                        key={perk.label}
                        className={`flex max-lg:flex-col items-center gap-3 min-w-0 lg:shrink-0 ${TABLET_PERK_CELLS[i] ?? ""}`}
                      >
                        <span className="flex items-center justify-center w-11 h-11 min-[1442px]:w-12 min-[1442px]:h-12 rounded-xl bg-white/10 shrink-0">
                          {perk.icon}
                        </span>
                        <div className="min-w-0 lg:whitespace-nowrap">
                          <p className="text-white text-[15px] min-[1442px]:text-[17px] font-semibold leading-none truncate lg:overflow-visible">
                            {perk.label}
                          </p>
                          <p className="text-gray-300 text-[12px] min-[1442px]:text-[14px] leading-[1.25] lg:leading-none mt-1">
                            {perk.sub}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Perks Intro Header */}
        <div className="relative bg-white pt-24 pb-8 z-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h3 className="text-[30px] sm:text-[40px] min-[1442px]:text-[38px] min-[1442px]:sm:text-[50px] leading-[1.15] font-medium tracking-[-1px] text-[#171717] mb-3">
              What it means to <span className="text-[#E00000] font-bold">launch with us.</span>
            </h3>
            <p className="text-[14px] min-[1442px]:text-[18px] font-semibold text-[#171717]">
              When You Become A Founding Partner, We Equip You With A Complete Growth Engine To Ensure Your
              Success From Day One.
            </p>
          </div>
        </div>

        {/* Bonus rows */}
        {BONUS_ROWS.map((row, i) => (
          <div
            key={row.title}
            className={`relative z-20 px-[21px] md:px-8 lg:px-12 py-16 ${i % 2 === 1 ? "bg-gray-50 border-t border-gray-200" : "bg-white"} ${
              i > 0 ? "border-t border-gray-200" : ""
            }`}
          >
            <div className="max-w-[1440px] mx-auto lg:px-6">
              <div
                className={`flex flex-col lg:items-center gap-12 lg:gap-20 ${
                  i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <div className={`lg:w-1/2 w-full ${i % 2 === 0 ? "text-right" : "text-left"}`}>
                  <div
                    className={`inline-flex items-center justify-center border border-gray-300 text-gray-700 font-medium px-6 py-2 rounded-full text-sm mb-6 ${
                      i % 2 === 0 ? "ml-auto" : ""
                    }`}
                  >
                    {row.label}
                  </div>
                  <h4 className="text-3xl font-bold text-gray-900 mb-4">{row.title}</h4>
                  <p
                    className={`text-gray-600 text-lg leading-relaxed mb-6 capitalize ${
                      i % 2 === 0 ? "ml-auto max-w-xl" : ""
                    }`}
                  >
                    {row.description}
                  </p>
                  {row.callout && (
                    <div
                      className={`bg-white px-6 pt-3 pb-1.5 rounded-xl border border-gray-300 shadow-sm max-w-2xl ${
                        i % 2 === 0 ? "ml-auto" : ""
                      }`}
                    >
                      <p className="text-sm font-bold text-gray-900 leading-none">{row.callout}</p>
                    </div>
                  )}
                </div>
                <div className="lg:w-1/2 w-full">
                  <div className="relative rounded-[30px] shadow-[0px_8px_28px_rgba(0,0,0,0.08)] overflow-hidden h-[360px]">
                    <img src={row.image} alt={row.imageAlt} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ── PRICING TIERS ── */}
      <FoundingPartnerSection onApply={selectTier} />

      {/* ── FREE DEMO ── */}
      <FreeDemoSection />

      {/* ── FAQ ── */}
      <section className="w-full flex justify-center py-20 bg-[#FBF1EF]">
        <div className="w-full max-w-[1440px] px-[21px] md:px-8 lg:px-12">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#D90000]" />
            <span className="text-[13px] min-[1442px]:text-[15px] font-semibold tracking-[1px] uppercase text-[#171717]">FAQ</span>
          </div>
          <h2 className="mt-4 text-[#171717] text-[30px] sm:text-[40px] min-[1442px]:text-[38px] min-[1442px]:sm:text-[50px] leading-[1.15] font-medium tracking-[-1px]">
            Everything you need to know
          </h2>

          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={item.q}
                  className={
                    isOpen
                      ? "rounded-[24px] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.06)]"
                      : "rounded-[24px] bg-white/40"
                  }
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    className="w-full flex items-center gap-5 px-8 py-6 text-left bg-transparent border-0 outline-none appearance-none rounded-[24px] cursor-pointer"
                  >
                    <span className="flex items-center justify-center w-11 h-11 min-[1442px]:w-12 min-[1442px]:h-12 rounded-full bg-[#D90000] text-white text-[16px] min-[1442px]:text-[18px] font-bold shrink-0">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 text-[15px] sm:text-[17px] lg:text-[21px] min-[1442px]:text-[18px] min-[1442px]:sm:text-[20px] min-[1442px]:lg:text-[26px] font-bold text-[#171717]">
                      {item.q}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-8 pb-8 -mt-1 pl-[76px]">
                      <p className="text-[16px] min-[1442px]:text-[18px] italic text-[#2B2B2B] leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      {isApplyOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden"
          onClick={() => setIsApplyOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-[95vw] sm:max-w-[600px] md:max-w-[720px] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsApplyOpen(false)}
              aria-label="Close application form"
              className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 z-10 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.2)] flex items-center justify-center text-[#171717] hover:bg-gray-100 transition"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <ApplicationForm tier={tier} onTierChange={setTier} onClose={() => setIsApplyOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Application form (separate so state resets are easy to reason about) ──

const ApplicationForm: React.FC<{
  tier: TierValue;
  onTierChange: (t: TierValue) => void;
  onClose?: () => void;
}> = ({ tier, onTierChange, onClose }) => {
  const dispatch = useDispatch();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isTierOpen, setIsTierOpen] = useState(false);
  const tierRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tierRef.current && !tierRef.current.contains(e.target as Node)) {
        setIsTierOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);

    const formData = new FormData(e.currentTarget);
    const contactName = ((formData.get("contactName") as string) || "").trim();
    const businessName = ((formData.get("businessName") as string) || "").trim();
    const email = ((formData.get("email") as string) || "").trim();
    const location = ((formData.get("location") as string) || "").trim();

    const message = [
      "Launch Package Application",
      "",
      `Selected Package: ${TIER_LABELS[tier]}`,
      `Business Name: ${businessName}`,
      `City / Location: ${location}`,
    ].join("\n");

    setSubmitting(true);
    const result = await dispatchAsAsync(
      emailActions.sendContactMessage({
        name: contactName,
        email,
        subject: "Launch Package Application",
        message,
      }),
      dispatch,
    );
    setSubmitting(false);

    if (!result) {
      setSubmitted(true);
    } else if (result[FORM_ERROR]) {
      setSubmitError(result[FORM_ERROR]);
    } else {
      setSubmitError("Something went wrong. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-[20px] sm:rounded-[30px] shadow-[0px_8px_28px_rgba(0,0,0,0.08)] p-6 sm:p-8 md:p-12 border-2 border-green-500 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-green-100 mb-4 sm:mb-6">
          <CheckIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4">Congratulations!</h2>
        <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
          Your request has been sent successfully. Our team will review your application and be in touch
          within 3 business days.
        </p>
        <a
          href="/signup"
          className="inline-block bg-gradient-to-r from-red-600 to-red-800 text-white font-medium py-3 px-8 rounded-full"
        >
          Create My Account
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[20px] sm:rounded-[30px] shadow-[0px_8px_28px_rgba(0,0,0,0.08)] border border-[#F3EEEE] max-h-[90vh] overflow-hidden flex flex-col">
      <div className="p-5 sm:p-8 md:p-12 overflow-y-auto">
      <div className="text-center mb-6 sm:mb-10">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#171717]">Ready to Launch With Us?</h2>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          Submit your application below. Spots for Launch Partners are strictly limited and actively
          closing.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-4 sm:space-y-6">
          <div ref={tierRef} className="relative">
            <label className="block text-sm font-bold text-gray-700 mb-2">Selected Package</label>
            <button
              type="button"
              onClick={() => setIsTierOpen((o) => !o)}
              className="w-full flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-3.5 sm:p-4 text-sm sm:text-base font-medium text-[#171717] cursor-pointer"
            >
              <span>{TIER_LABELS[tier]}</span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isTierOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isTierOpen && (
              <div className="absolute z-20 mt-2 w-full bg-white rounded-[24px] shadow-[0px_20px_50px_rgba(0,0,0,0.18)] border border-gray-100 p-4">
                <button
                  type="button"
                  onClick={() => {
                    onTierChange("founding");
                    setIsTierOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-800 text-white font-bold text-[15px] min-[1442px]:text-[17px] rounded-full py-3.5 px-6 mb-3 hover:opacity-95 transition"
                >
                  {TIER_LABELS.founding}
                </button>

                <div className="flex flex-col gap-2">
                  {(["growth", "pioneer"] as TierValue[]).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        onTierChange(value);
                        setIsTierOpen(false);
                      }}
                      className={`w-full text-center rounded-full py-3 px-5 text-[15px] min-[1442px]:text-[17px] font-medium border transition ${
                        tier === value
                          ? "bg-red-50 border-red-200 text-[#E00000]"
                          : "bg-white border-gray-200 text-[#171717] hover:bg-gray-50"
                      }`}
                    >
                      {TIER_LABELS[value]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name
              </label>
              <input
                type="text"
                id="contact-name"
                name="contactName"
                required
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-red-600 focus:border-red-600 p-3 border"
              />
            </div>
            <div>
              <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                id="business-name"
                name="businessName"
                required
                className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-red-600 focus:border-red-600 p-3 border"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-red-600 focus:border-red-600 p-3 border"
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              City / Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              required
              placeholder="e.g. Halifax"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-red-600 focus:border-red-600 p-3 border"
            />
          </div>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm p-3">
              {submitError}
            </div>
          )}

          <div className="pt-4 sm:pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-red-600 to-red-800 text-white font-medium py-3.5 sm:py-4 px-8 rounded-full shadow-xl text-base sm:text-lg hover:scale-[1.01] transition disabled:opacity-60"
            >
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
};

export default LaunchPackage;